import { useAutoneTranslation } from '@autone/translations';
import {
  TopNavbar,
  TopNavBarSubtitle,
  TopNavBarTitle,
  TopNavBarTitlesWrapper,
} from '@autone/ui-next/components';
import { Outlet } from '@tanstack/react-router';
import { createFileRoute, redirect } from '@tanstack/react-router';
import { Provider } from 'react-redux';

import { type APP_NAME } from '../../new/constants';
import { AssortmentTabs } from '../../new/features/assortment-tabs';
import { CreateInitialAllocationRecommendations } from '../../new/features/create-initial-allocation-recommendations';
import { AssortmentDataFeedTooltip } from '../../new/features/data-feed/AssortmentDataFeedTooltip';
import { AssortmentFiltersContextProvider } from '../../new/features/filters';
import { useAssortmentDataFeedStatus } from '../../new/hooks/data-feed';
import { store } from '../../new/redux/store';
import { AssortmentSavedViewSelect } from '../../new/saved-views';
import { DownloadTemplateModal } from '../../old/features/assortment-template-modals/download-template-modal';
import { UploadTemplateModal } from '../../old/features/assortment-template-modals/upload-template-modal';
import legacyStore from '../../old/redux/store';

export const Route = createFileRoute('/new')({
  beforeLoad: ({ context }) => {
    if (!context.releaseAssortmentIA) {
      throw redirect({ to: '/in-season' });
    }
  },
  component: RouteLayout,
});

function RouteLayout() {
  const { t } = useAutoneTranslation<typeof APP_NAME>();

  return (
    <Provider store={store}>
      <div className="flex flex-col h-full overflow-hidden">
        <TopNavbar className="flex items-center gap-2 justify-between">
          <TopNavBarTitlesWrapper>
            <TopNavBarTitle>{t('new.header')}</TopNavBarTitle>
            <div className="flex flex-row gap-0.5 items-center">
              <TopNavBarSubtitle>{t('new.sub-header')}</TopNavBarSubtitle>
              <AssortmentDataFeedTooltip />
            </div>
          </TopNavBarTitlesWrapper>
          <div className="flex items-center gap-2">
            <AssortmentTemplateModalsNew />
            <CreateInitialAllocationRecommendations />
          </div>
        </TopNavbar>
        <main className="flex flex-col p-8 gap-4 bg-background flex-1 min-h-0">
          <div className="flex items-center justify-between gap-2">
            <AssortmentTabs />
            <AssortmentSavedViewSelect />
          </div>
          <AssortmentFiltersContextProvider>
            <Outlet />
          </AssortmentFiltersContextProvider>
        </main>
      </div>
    </Provider>
  );
}

const AssortmentTemplateModalsNew = () => {
  const {
    isAssortmentDataFeedActive,
    isInitialAllocationDataFeedActive,
    isLoading,
  } = useAssortmentDataFeedStatus();

  if (isLoading) {
    return null;
  }

  const usesBothDataFeeds =
    isAssortmentDataFeedActive && isInitialAllocationDataFeedActive;

  if (usesBothDataFeeds) {
    return (
      <Provider store={legacyStore}>
        <DownloadTemplateModal />
      </Provider>
    );
  }

  return (
    <Provider store={legacyStore}>
      <DownloadTemplateModal />
      <UploadTemplateModal variant={'secondary'} />
    </Provider>
  );
};
